import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { LAYOUTS } from '../../utils/theme';
import { errorBox } from '../AuthLayout';

const formatDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const formatDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const PLAN_NAMES = { free: 'Free', pro: 'Pro', teams: 'Teams', enterprise: 'Enterprise' };
const PROVIDER_NAMES = { email: 'Email + password', google: 'Google' };
const BILLING_NAMES = {
  none: 'Free plan', complimentary: 'Free access', active: 'Paying', trialing: 'Trial',
  past_due: 'Payment overdue', canceled: 'Subscription canceled', incomplete: 'Checkout not finished',
};

const layoutName = (id) => LAYOUTS.find((l) => l.id === id)?.name;

function Section({ title, children, right }) {
  return (
    <section className="bg-white rounded-2xl border border-line p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="font-semibold text-ink">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

/** Label/value rows; empty values show a dash. */
function Facts({ rows }) {
  return (
    <dl className="grid sm:grid-cols-[180px_1fr] gap-x-4 gap-y-2 text-sm">
      {rows.filter(Boolean).map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-ink-muted">{label}</dt>
          <dd className="text-ink break-all">{value === null || value === undefined || value === '' ? '—' : value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Mono({ children }) {
  return children ? <code className="font-mono text-xs bg-surface px-1.5 py-0.5 rounded">{children}</code> : '—';
}

/**
 * Admin → one account (personal or company): owner/people with sign-in info, plan and billing,
 * Stripe ids, and every card with its counts and links. Data from public.admin_account_detail.
 */
export default function AccountDetail({ orgId, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    supabase.rpc('admin_account_detail', { p_org_id: orgId }).then(({ data, error }) => {
      if (!live) return;
      if (error) setError(error.message);
      else if (!data) setError('This account does not exist (it may have been deleted).');
      else setData(data);
    });
    return () => { live = false; };
  }, [orgId]);

  const back = (
    <button onClick={onBack} className="text-sm font-medium text-ink hover:underline">← Back to the list</button>
  );

  if (error) return <div className="space-y-4">{back}<div className={errorBox}>{error}</div></div>;
  if (!data) return <div className="space-y-4">{back}<p className="text-ink-muted">Loading...</p></div>;

  const { organization: o, members, cards } = data;
  const personal = o.kind === 'personal';
  const compExpired = o.comp_until && new Date(o.comp_until) < new Date();

  return (
    <div className="space-y-6">
      {back}

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight text-ink">{o.name}</h2>
        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-surface text-ink-muted border border-line">{personal ? 'Personal account' : 'Company'}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.status === 'active' ? 'bg-blue-soft text-blue' : 'bg-coral-soft text-coral-ink'}`}>{o.status}</span>
        {!personal && <Link to={`/company/${o.id}`} className="text-sm font-medium text-blue hover:underline">Open company page</Link>}
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <Section title="Account">
          <Facts rows={[
            ['Name', o.name],
            ['Short name', <Mono key="s">{o.slug}</Mono>],
            ['Account id', <Mono key="i">{o.id}</Mono>],
            ['Created', formatDateTime(o.created_at)],
            ['Created by', o.created_by_email],
            ['Last changed', formatDateTime(o.updated_at)],
            !personal && ['Seat limit', o.seat_limit ?? 'No limit'],
            !personal && ['Open invitations', o.open_invites],
          ]} />
        </Section>

        <Section title="Plan and billing">
          <Facts rows={[
            ['Plan', PLAN_NAMES[o.plan] || o.plan],
            ['Billing status', `${BILLING_NAMES[o.billing_status] || o.billing_status}${o.billing_status === 'complimentary' && compExpired ? ' (ended)' : ''}`],
            o.billing_status === 'complimentary' && ['Free access until', o.comp_until ? formatDate(o.comp_until) : 'No end date'],
            o.comp_note && ['Free access note', o.comp_note],
            o.comp_granted_at && ['Free access given', `${formatDate(o.comp_granted_at)}${o.comp_granted_by_email ? ` by ${o.comp_granted_by_email}` : ''}`],
            o.offer && ['Offer', o.offer === 'team25' ? 'Founder offer (25% off Teams)' : o.offer],
            ['Stripe customer', <Mono key="c">{o.stripe_customer_id}</Mono>],
            ['Stripe subscription', <Mono key="u">{o.stripe_subscription_id}</Mono>],
            (o.stripe_customer_id || o.stripe_subscription_id) && ['Stripe mode', o.stripe_livemode ? 'Live' : 'Sandbox'],
          ]} />
        </Section>
      </div>

      <Section title={personal ? 'Owner' : `People (${members.length})`}>
        {members.length === 0 ? <p className="text-sm text-ink-muted">Nobody belongs to this account.</p> : (
          <div className="divide-y divide-line">
            {members.map((m) => (
              <div key={m.user_id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-medium text-ink">{m.full_name || m.email}</span>
                  {m.full_name && <span className="text-sm text-ink-muted">{m.email}</span>}
                  <span className="text-xs text-ink-muted capitalize">· {m.role}{m.status !== 'active' ? ` · ${m.status}` : ''}</span>
                  {m.platform_admin && <span className="text-xs text-blue font-semibold">Platform admin</span>}
                </div>
                <Facts rows={[
                  ['Email', m.email],
                  ['Email confirmed', m.email_confirmed_at ? formatDateTime(m.email_confirmed_at) : 'Not yet (invitation not accepted)'],
                  ['Signs in with', m.providers.map((p) => PROVIDER_NAMES[p] || p).join(', ')],
                  m.google_emails.length > 0 && ['Google accounts', m.google_emails.join(', ')],
                  ['Account created', formatDateTime(m.created_at)],
                  ['Last sign-in', m.last_sign_in_at ? formatDateTime(m.last_sign_in_at) : 'Never'],
                  ['User id', <Mono key="u">{m.user_id}</Mono>],
                ]} />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title={`Cards (${cards.length})`}>
        {cards.length === 0 ? <p className="text-sm text-ink-muted">No cards yet.</p> : (
          <div className="grid lg:grid-cols-2 gap-4">
            {cards.map((c) => <CardBox key={c.id} card={c} showOwner={!personal} />)}
          </div>
        )}
      </Section>
    </div>
  );
}

function CardBox({ card: c, showOwner }) {
  const initials = (c.full_name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const stats = [['Views', c.views, c.views_30d], ['Saves', c.saves, c.saves_30d], ['Leads', c.leads, c.leads_30d]];
  const link = 'text-sm font-medium text-blue hover:underline';
  return (
    <div className="rounded-xl border border-line p-4 space-y-4">
      <div className="flex items-start gap-3">
        {c.photo_url
          ? <img src={c.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border border-line shrink-0" />
          : <div className="w-14 h-14 rounded-full bg-blue-soft text-blue font-bold flex items-center justify-center shrink-0">{initials}</div>}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink">{c.full_name || 'No name'}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.status === 'active' ? 'bg-blue-soft text-blue' : 'bg-coral-soft text-coral-ink'}`}>{c.status}</span>
          </div>
          {(c.title || c.company) && <div className="text-sm text-ink-muted">{[c.title, c.department, c.company].filter(Boolean).join(' · ')}</div>}
          <div className="text-xs text-ink-muted font-mono mt-0.5">/c/{c.slug}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <a href={`/c/${c.slug}`} target="_blank" rel="noreferrer" className={link}>Public card ↗</a>
        <Link to={`/cards/${c.id}/edit`} className={link}>Edit</Link>
        <Link to={`/cards/${c.id}/print`} className={link}>NFC print design</Link>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {stats.map(([label, total, last30]) => (
          <div key={label} className="rounded-lg bg-surface px-3 py-2">
            <div className="text-xs text-ink-muted">{label}</div>
            <div className="text-lg font-bold tabular-nums text-ink">{total}</div>
            <div className="text-xs text-ink-muted tabular-nums">{last30} in 30 days</div>
          </div>
        ))}
      </div>

      <Facts rows={[
        showOwner && ['Cardholder', c.owner_email || 'No login yet'],
        ['Email', c.email],
        ['Phone', c.phone],
        ['Website', c.website],
        ['LinkedIn', c.linkedin],
        ['X / Twitter', c.twitter],
        ['Instagram', c.instagram],
        ['Photo', c.photo_url ? 'Yes' : 'No'],
        ['Layout', c.layout ? layoutName(c.layout) || c.layout : 'Company default'],
        ['Lead form setting', c.lead_capture ? 'On (shows with Pro or Teams)' : 'Off'],
        ['Created', formatDateTime(c.created_at)],
        ['Last changed', formatDateTime(c.updated_at)],
        ['Card id', <Mono key="i">{c.id}</Mono>],
      ]} />
    </div>
  );
}
